import { ApiShopperLandingPage } from '@storefronts/common-types'
import { useEffect, useSyncExternalStore } from 'react'
import {
    IS_EDITOR_PREVIEW,
    IS_PREVIEW,
} from '../../context/QueryParamContextProvider'
import { Attentive } from './AttentivePixel'
import { BlotoutScript } from './BlotoutPixel'
import { ClarityScript } from './ClarityScript'
import { HyrosScript } from './Hyros'
import { Elevar } from './Elevar'
import { FacebookPixel } from './FacebookPixel'
import { GTMPixel } from './GTMPixel'
import { KlaviyoPixel } from './KlaviyoPixel'
import { PostscriptPixel } from './PostscriptPixel'
import { TripleWhalePixel } from './TripleWhalePixel'
import { GtagPixel } from './GtagPixel'
import { HotjarPixel } from './HotjarPixel'
import { NorthbeamPixel } from './NorthbeamPixel'
import { TikTokPixel } from './TikTokPixel'
import { RockerboxPixel } from './RockerboxPixel'
import { RoktPixel } from './RoktPixel'
import { PinterestPixel } from './PinterestPixel'
import { RedditPixel } from './RedditPixel'
import { SnapchatPixel } from './SnapchatPixel'
import { AppLovinPixel } from './AppLovinPixel'
import { CriteoPixel } from './CriteoPixel'
import { TaboolaPixel } from './TaboolaPixel'
import { ConvergePixel } from './ConvergePixel'
import { setConfiguredPixelVendors, type PixelVendor } from './pixelRuntime'
import { usePixelScriptsReady } from './pixelScriptScheduler'
import { useTrackingAllowed } from '../consent/consentTrackingStore'

function configuredPixelVendors(
    integrations: ApiShopperLandingPage['integrations'],
): PixelVendor[] {
    const vendors: PixelVendor[] = []
    if (integrations.gtmPixelId) vendors.push('gtm')
    if (integrations.metaPixelId) vendors.push('meta')
    if (integrations.ga4PixelId || integrations.googleAdsTagId) {
        vendors.push('googleGtag')
    }
    if (integrations.applovinEventKey) vendors.push('axon')
    if (integrations.blotoutId) vendors.push('blotout')
    if (integrations.attentiveId) vendors.push('attentive')
    if (integrations.postscriptId) vendors.push('postscript')
    if (integrations.tripleWhalePixelId) vendors.push('tripleWhale')
    if (integrations.klaviyoId) vendors.push('klaviyo')
    if (integrations.elevarConfigId) vendors.push('elevar')
    if (integrations.convergePixelId) vendors.push('converge')
    return vendors
}

export const PixelLoader: React.FC<{ landingPage: ApiShopperLandingPage }> = ({
    landingPage: landingPageProp,
}) => {
    const landingPage: ApiShopperLandingPage = {
        ...landingPageProp,
        integrations: landingPageProp.integrations ?? {},
    }
    const mounted = useSyncExternalStore(
        () => () => {},
        () => true,
        () => false,
    )
    const scriptsReady = usePixelScriptsReady()
    const trackingAllowed = useTrackingAllowed()

    useEffect(() => {
        setConfiguredPixelVendors(
            configuredPixelVendors(landingPageProp.integrations ?? {}),
        )
    }, [landingPageProp])

    if (!mounted) return null
    if (IS_PREVIEW() || IS_EDITOR_PREVIEW()) return null
    if (!trackingAllowed) return null
    if (!scriptsReady) return null

    return (
        <>
            <ClarityScript landingPage={landingPage} />
            <FacebookPixel
                landingPage={landingPage}
                scriptsReady={scriptsReady}
            />
            <HyrosScript landingPage={landingPage} />
            <GTMPixel landingPage={landingPage} scriptsReady={scriptsReady} />
            <Elevar landingPage={landingPage} />
            <Attentive landingPage={landingPage} />
            <KlaviyoPixel landingPage={landingPage} />
            <PostscriptPixel landingPage={landingPage} />
            <BlotoutScript landingPage={landingPage} />
            <TripleWhalePixel landingPage={landingPage} />
            <GtagPixel landingPage={landingPage} />
            <NorthbeamPixel landingPage={landingPage} />
            <HotjarPixel landingPage={landingPage} />
            <TikTokPixel landingPage={landingPage} />
            <RockerboxPixel landingPage={landingPage} />
            <RoktPixel landingPage={landingPage} />
            <PinterestPixel landingPage={landingPage} />
            <RedditPixel landingPage={landingPage} />
            <SnapchatPixel landingPage={landingPage} />
            <AppLovinPixel landingPage={landingPage} />
            <CriteoPixel landingPage={landingPage} />
            <TaboolaPixel landingPage={landingPage} />
            <ConvergePixel landingPage={landingPage} />
        </>
    )
}
